/**
 * System Settings Schemas
 *
 * Zod schemas for system settings management API.
 */

import { z } from 'zod';

// Setting types
export const SettingTypeEnum = z.enum(['string', 'boolean', 'number', 'json']);
export type SettingType = z.infer<typeof SettingTypeEnum>;

// Setting categories for grouping
export const SettingCategoryEnum = z.enum(['billing', 'notifications', 'security', 'vendor']);
export type SettingCategory = z.infer<typeof SettingCategoryEnum>;

// Setting keys (mirrors backend SettingKey enum)
export const SettingKeyEnum = z.enum([
  // Billing / GST Settings
  'GST_ENABLED',
  'GST_RATE',
  'SERVICE_FEE_PERCENTAGE',
  'PLATFORM_FEE_PERCENTAGE',
  'PAYMENT_TIMEOUT_MINUTES',

  // SMS Settings
  'SMS_ENABLED',
  'SMS_PROVIDER',

  // Auth Security Settings
  'AUTH_LOCKOUT_ENABLED',
  'AUTH_LOCKOUT_ATTEMPTS',
  'AUTH_LOCKOUT_DURATION_MINUTES',

  // Token Settings
  'TOKEN_REFRESH_ENABLED',
  'ACCESS_TOKEN_EXPIRY_HOURS',
  'REFRESH_TOKEN_EXPIRY_DAYS',

  // Push Notification Settings
  'PUSH_NOTIFICATIONS_ENABLED',

  // Vendor Settings
  'VENDOR_COMMISSION_PERCENTAGE',
  'VENDOR_PAYOUT_FREQUENCY',
]);
export type SettingKey = z.infer<typeof SettingKeyEnum>;

// SMS Provider options
export const SmsProviderEnum = z.enum(['', 'msg91', 'twilio', 'aws_sns', 'textlocal', 'gupshup']);
export type SmsProvider = z.infer<typeof SmsProviderEnum>;

// Payout frequency options
export const PayoutFrequencyEnum = z.enum(['daily', 'weekly', 'monthly']);
export type PayoutFrequency = z.infer<typeof PayoutFrequencyEnum>;

// System setting schema
export const SystemSettingSchema = z.object({
  id: z.string(),
  key: z.string(),
  value: z.string(),
  type: z.string(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});
export type SystemSetting = z.infer<typeof SystemSettingSchema>;

// Setting with metadata (for UI display)
export const SystemSettingWithMetadataSchema = SystemSettingSchema.extend({
  category: z.string(),
  description: z.string(),
  defaultValue: z.string().optional(),
});
export type SystemSettingWithMetadata = z.infer<typeof SystemSettingWithMetadataSchema>;

// Update setting request
export const UpdateSettingRequestSchema = z.object({
  value: z.string().min(0, 'Value is required'),
  type: SettingTypeEnum.optional(),
});
export type UpdateSettingRequest = z.infer<typeof UpdateSettingRequestSchema>;

// Create setting request
export const CreateSettingRequestSchema = z.object({
  key: z.string().min(1, 'Key is required'),
  value: z.string().min(0, 'Value is required'),
  type: SettingTypeEnum.optional().default('string'),
});
export type CreateSettingRequest = z.infer<typeof CreateSettingRequestSchema>;

// Bulk update settings request
export const BulkUpdateSettingsRequestSchema = z.object({
  settings: z.array(
    z.object({
      key: z.string(),
      value: z.string(),
      type: SettingTypeEnum.optional(),
    })
  ),
});
export type BulkUpdateSettingsRequest = z.infer<typeof BulkUpdateSettingsRequestSchema>;

// Settings grouped response
export const GroupedSettingsResponseSchema = z.object({
  settings: z.array(SystemSettingWithMetadataSchema),
  grouped: z.record(z.string(), z.array(SystemSettingWithMetadataSchema)),
});
export type GroupedSettingsResponse = z.infer<typeof GroupedSettingsResponseSchema>;

// Category display labels
export const SETTING_CATEGORY_LABELS: Record<SettingCategory, string> = {
  billing: 'Billing & GST',
  notifications: 'Notifications',
  security: 'Security',
  vendor: 'Vendor Management',
};

// Category descriptions
export const SETTING_CATEGORY_DESCRIPTIONS: Record<SettingCategory, string> = {
  billing: 'Configure GST, platform fees, and payment timeouts',
  notifications: 'Configure SMS providers and push notifications',
  security: 'Configure login security and session management',
  vendor: 'Configure vendor commissions and payout schedules',
};

// SMS Provider info for comparison
export const SMS_PROVIDER_INFO: Record<
  Exclude<SmsProvider, ''>,
  { name: string; costPerSms: string; coverage: string; recommendation: string }
> = {
  msg91: {
    name: 'MSG91',
    costPerSms: '₹0.13-0.18',
    coverage: 'Excellent (India)',
    recommendation: 'Best for India',
  },
  twilio: {
    name: 'Twilio',
    costPerSms: '₹0.50-0.70',
    coverage: 'Global',
    recommendation: 'Best for global apps',
  },
  aws_sns: {
    name: 'AWS SNS',
    costPerSms: '₹0.15-0.20',
    coverage: 'Good (India)',
    recommendation: 'If already on AWS',
  },
  textlocal: {
    name: 'Textlocal',
    costPerSms: '₹0.12-0.15',
    coverage: 'Excellent (India)',
    recommendation: 'Budget option',
  },
  gupshup: {
    name: 'Gupshup',
    costPerSms: '₹0.10-0.14',
    coverage: 'Excellent (India)',
    recommendation: 'High volume',
  },
};

// Public pricing rates (for unauthenticated users)
export const PublicPricingRatesSchema = z.object({
  serviceFeePercent: z.number(),
  gstEnabled: z.boolean(),
  gstPercent: z.number(),
});

export type PublicPricingRates = z.infer<typeof PublicPricingRatesSchema>;
